import type { ApiDocument, GetDocumentsResponse } from "@workith/shared/types/api-endpoints";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DocumentGrid } from "../components/document/DocumentGrid";
import { DocumentGridSkeleton } from "../components/document/DocumentGridSkeleton";
import { DocumentList } from "../components/document/DocumentList";
import { DocumentViewModeToggle } from "../components/document/DocumentViewModeToggle";
import { DocumentVisibilityBadge } from "../components/document/DocumentVisibilityBadge";
import { useDocumentViewMode } from "../components/document/useDocumentViewMode";
import api from "../lib/api";

export function RecentPage() {
  const [documents, setDocuments] = useState<ApiDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useDocumentViewMode();
  const navigate = useNavigate();

  const openDocument = useCallback(
    (documentItem: ApiDocument) => {
      navigate(`/editor/${documentItem.id}`);
    },
    [navigate]
  );

  useEffect(() => {
    api
      .get<GetDocumentsResponse>("/api/documents/recent")
      .then((data) => setDocuments(data.documents))
      .catch((err) => console.error("Failed to fetch recent documents:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-6">
        <DocumentGridSkeleton />
      </div>
    );
  }

  const DocumentView = viewMode === "list" ? DocumentList : DocumentGrid;

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold">Recent</h2>
        <DocumentViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
      </div>
      {documents.length === 0 ? (
        <p className="text-muted-foreground">No recent documents.</p>
      ) : (
        <DocumentView
          documents={documents}
          onOpenDocument={openDocument}
          renderBadge={(documentItem) => (
            <DocumentVisibilityBadge visibility={documentItem.visibility} />
          )}
        />
      )}
    </div>
  );
}
